import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, AlertCircle, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import api from '../lib/api';

const CompanySignupPage = () => {
  const navigate = useNavigate();
  
  // Company details
  const [companyName, setCompanyName] = useState('');
  const [companySlug, setCompanySlug] = useState('');
  
  // Admin user details
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = company info, 2 = admin info

  // Auto-generate slug from company name
  const handleCompanyNameChange = (value) => {
    setCompanyName(value);
    // Generate slug: lowercase, replace spaces with hyphens, remove special chars
    const slug = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
    setCompanySlug(slug);
  };

  const validateStep1 = () => {
    if (!companyName.trim()) {
      setError('Company name is required');
      return false;
    }
    if (!companySlug.trim()) {
      setError('Company slug is required');
      return false;
    }
    if (companySlug.length < 3) {
      setError('Company slug must be at least 3 characters');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!adminName.trim()) {
      setError('Your name is required');
      return false;
    }
    if (!adminEmail.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(adminEmail)) {
      setError('Please enter a valid email');
      return false;
    }
    if (adminPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (adminPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateStep2()) {
      return;
    }

    setLoading(true);

    try {
      // Create company with admin user (public signup endpoint)
      const response = await api.post('/public/company-signup', {
        name: companyName,
        slug: companySlug,
        admin_email: adminEmail,
        admin_password: adminPassword,
        admin_name: adminName,
        logo_url: '',
        primary_color: '#F59E0B',
        secondary_color: '#1E293B',
        max_users: 50,
        plan: 'trial' // Start with 14-day trial
      });

      if (response.data) {
        toast.success(`Welcome to WorkStream Pro, ${adminName}! Your company "${companyName}" has been created.`);
        
        // Auto-login the new admin
        const loginResponse = await api.post('/auth/login', {
          email: adminEmail,
          password: adminPassword
        });

        if (loginResponse.data.access_token) {
          // Backend has already set the httpOnly auth cookie; just cache the user profile.
          localStorage.setItem('user', JSON.stringify(loginResponse.data.user));
          
          // Redirect to dashboard
          navigate('/dashboard');
        }
      }
    } catch (err) {
      console.error('Signup error:', err);
      if (err.response?.status === 400) {
        const errorDetail = err.response.data.detail || 'Invalid request';
        // Show the specific error from the backend
        setError(errorDetail);
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to create account. Please check your internet connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Back to Landing */}
        <Button
          onClick={() => navigate('/landing')}
          variant="ghost"
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-12">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Start Your Free Trial
            </h1>
            <p className="text-gray-600">
              Create your company account in 2 easy steps
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center gap-2">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
              </div>
              <div className={`w-16 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                2
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 mb-6">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Company Information */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Company Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-700 mb-2 block">Company Name *</Label>
                    <Input
                      type="text"
                      value={companyName}
                      onChange={(e) => handleCompanyNameChange(e.target.value)}
                      placeholder="e.g., Acme Manufacturing"
                      className="w-full h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-gray-900 placeholder:text-gray-400"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700 mb-2 block">Company URL Slug *</Label>
                    <div className="flex items-center">
                      <span className="px-4 py-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-gray-600 text-sm">
                        workstreampro.com/
                      </span>
                      <Input
                        type="text"
                        value={companySlug}
                        onChange={(e) => setCompanySlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        placeholder="acme-manufacturing"
                        className="flex-1 h-12 border-gray-300 rounded-l-none focus:border-blue-500 focus:ring-blue-500 text-gray-900 placeholder:text-gray-400"
                        disabled={loading}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">This will be your unique company login URL</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleNext}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white text-lg font-semibold rounded-lg"
              >
                Continue to Admin Setup
              </Button>
            </div>
          )}

          {/* Step 2: Admin Account */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Administrator Account</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-700 mb-2 block">Your Full Name *</Label>
                    <Input
                      type="text"
                      value={adminName}
                      onChange={(e) => setAdminName(e.target.value)}
                      placeholder="John Doe"
                      className="w-full h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-gray-900 placeholder:text-gray-400"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700 mb-2 block">Email Address *</Label>
                    <Input
                      type="email"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      placeholder="john@acme.com"
                      className="w-full h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-gray-900 placeholder:text-gray-400"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700 mb-2 block">Password *</Label>
                    <Input
                      type="password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="w-full h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-gray-900 placeholder:text-gray-400"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700 mb-2 block">Confirm Password *</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your password"
                      className="w-full h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-gray-900 placeholder:text-gray-400"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1 h-12 border-gray-300 hover:bg-gray-50"
                  disabled={loading}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white text-lg font-semibold rounded-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-semibold mb-1">Your 14-day trial includes:</p>
                    <ul className="space-y-1 text-blue-800">
                      <li>• Up to 5 users</li>
                      <li>• Up to 50 work orders</li>
                      <li>• No credit card required</li>
                      <li>• Email bc2801@hotmail.com to switch to Free</li>
                    </ul>
                  </div>
                </div>
              </div>
            </form>
          )}

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
              Sign in
            </Link>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            Separate company records
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            No card for the trial
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySignupPage;
